import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { getMasterclassCaseRoute, getMasterclassCasesRoute } from '@/config/routes';

/**
 * Hook for resuming masterclass or case learning.
 *
 * Note: Sessions are stored per-masterclass, not per-case.
 * The case selector shows progress for the masterclass session.
 * CaseRenderer handles resuming from the correct slide.
 */
export const useResume = () => {
  const navigate = useNavigate();

  /**
   * Navigate to a masterclass's case selector.
   * The case selector shows progress and allows the user to choose a case.
   *
   * @param masterclassId - The masterclass to navigate to
   */
  const resumeMasterclass = useCallback(
    (masterclassId: string) => {
      navigate(getMasterclassCasesRoute(masterclassId));
    },
    [navigate]
  );

  /**
   * Resume a specific case from where user left off.
   * CaseRenderer uses the session's currentSlideId to determine which slide to show.
   *
   * @param masterclassId - The masterclass containing the case
   * @param caseId - The case to resume
   */
  const resumeCase = useCallback(
    (masterclassId: string, caseId: string) => {
      navigate(getMasterclassCaseRoute(masterclassId, caseId));
    },
    [navigate]
  );

  return {
    resumeMasterclass,
    resumeCase,
  };
};
